export default function ({ $config, $axios, store, $sentry, $log }, inject) {
  const logger = $log.create('Ecom API')

  const api = $axios.create({ timeout: 60000, withCredentials: true })
  api.defaults.useCache = false

  const runtimeConfig = $config.axios || {}
  const baseURL = process.browser ? runtimeConfig.ecomBrowserBaseURL : runtimeConfig.ecomBaseURL
  api.setBaseURL(baseURL)

  api.onRequest((config) => {
    // Track when the request is being made
    config.meta = config.meta || {}
    config.meta.requestStartedAt = new Date().getTime()

    if (!process.server && (config.progress || config.progress === undefined)) {
      config.meta.uid = Math.floor(Math.random() * (new Date()))
      store.commit('page/REQUEST_START', [config.meta.uid, config.timeout])
    }

    // Attach headers from store
    const mapping = [
      ['sg-frontend-id', 'store/frontendId'],
      ['sg-frontend-cid', 'store/frontendCid'],
      ['sg-pid', 'store/sg18pid'],
      ['sg-frontend-sc', 'store/storeRunCode']
    ]
    
    config.headers = { ...config.headers } || {}
    mapping.forEach(
      ([headerName, getter]) => {
        if (store.getters[getter] && !['deleted', 'null'].includes(store.getters[getter])) {
          config.headers[headerName] = store.getters[getter].split(',').pop()
        }
      }
    )

    if (config.storeCode) config.headers['sg-frontend-sc'] = config.storeCode // Allow manually overriding store code

    if (config.headers) config.headers['sg-use-cache'] = config.useCache ? 1 : 0

    logger.debug(`${config.method.toUpperCase()} ${config.url}`)
  })

  api.onResponse((response) => {
    // If we've encountered a session exception then we need to
    // clear the session and throw an error
    if (response.data && typeof response.data === 'object' && 'SESSION_CLEAR' in response.data) {
      store.commit('cart/CLEAR_SESSION')
      store.commit('store/CLEAR_SESSION')

      if (response.config && response.config.meta.uid) store.commit('page/REQUEST_FINISH', response.config.meta.uid)
      throw new Error('Something went wrong, please try again.')
    }

    // Store form_key if it is included in this response
    if (response.data && typeof response.data === 'object' && 'form_key' in response.data) {
      store.commit('cart/SET_FORM_KEY', response.data.form_key)
    }

    // Store cookies that have been passed as headers
    const mapping = [
      ['sg-frontend-id', 'store/SET_FRONTEND_ID'],
      ['sg-frontend-cid', 'store/SET_FRONTEND_CID'],
      ['sg-pid', 'store/SET_SG18PID']
    ]
    
    mapping.forEach(
      ([headerName, mutation]) => {
        if (response.headers[headerName]) {
          logger.debug(`${mutation}(${response.headers[headerName]})`)
          store.commit(mutation, response.headers[headerName].split(',').pop())
        }
      }
    )

    // Log how long this request took
    const requestTime = ((new Date().getTime()) - response.config.meta.requestStartedAt) / 1000
    logger.debug(`${response.config.method.toUpperCase()} ${response.config.url} ${response.status} ${response.statusText} in ${requestTime}s`)
    
    if (response.config && response.config.meta.uid) store.commit('page/REQUEST_FINISH', response.config.meta.uid)
  })

  api.onError((error) => {
    if (error.config && error.config.meta.uid) store.commit('page/REQUEST_FINISH', error.config.meta.uid)

    if (!process.server) store.commit('page/REQUEST_FINISH')
    $sentry.captureException(error)
    logger.error(error)
  })

  api.quietGet = async (path, options) => {
    return await api.get(path, { progress: false, ...options })
  }

  api.quietPost = async (path, payload, options) => {
    return await api.post(path, payload, { progress: false, ...options })
  }

  api.$quietGet = async (path, options) => {
    return await api.$get(path, { progress: false, ...options })
  }

  api.$quietPost = async (path, payload, options) => {
    return await api.$post(path, payload, { progress: false, ...options })
  }

  // Inject to context as $ecomAPI
  inject('ecomAPI', api)
}